import { useMemo, useState, useRef, useEffect } from "react";
import { ArrowIcon, ChevronIcon } from "./icons";

const PAGE_SIZE_OPTIONS = [25, 50, 100];

interface Props {
  page: number;
  totalCount: number;
  totalCountExact: boolean;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

export default function Pagination({
  page,
  totalCount,
  totalCountExact,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: Props) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const [sizeOpen, setSizeOpen] = useState(false);
  const sizeRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!sizeOpen) return;
    const handler = (e: MouseEvent) => {
      if (sizeRef.current && !sizeRef.current.contains(e.target as Node)) {
        setSizeOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [sizeOpen]);

  const pageNumbers = useMemo(() => {
    const pages: (number | "ellipsis")[] = [];

    // Always show page 1
    pages.push(1);

    // Window: ±4 around current page
    const windowStart = Math.max(2, page - 4);
    const windowEnd = Math.min(totalPages - 1, page + 4);

    // Ellipsis between page 1 and window start
    if (windowStart > 2) {
      pages.push("ellipsis");
    }

    for (let i = windowStart; i <= windowEnd; i++) {
      pages.push(i);
    }

    // Ellipsis between window end and last page
    if (windowEnd < totalPages - 1) {
      pages.push("ellipsis");
    }

    // Always show last page (if > 1)
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  }, [page, totalPages]);

  // Hide pagination when there's only one page and count is exact
  if (totalPages <= 1 && totalCountExact) return null;

  const showPrev = page > 1;
  const showNext = page < totalPages || !totalCountExact;
  const safePage = (p: number) => onPageChange(Math.max(1, p));

  return (
    <nav className="lt-pagination">
      <div className="lt-pagination__center">
        <button
          type="button"
          className="lt-pagination__arrow-btn"
          disabled={!showPrev}
          onClick={() => safePage(page - 1)}
          aria-label="Предыдущая страница"
        >
          <ArrowIcon direction="left" />
        </button>

        <div className="lt-pagination__pages">
          {pageNumbers.map((item, idx) =>
            item === "ellipsis" ? (
              <span key={`e${idx}`} className="lt-pagination__ellipsis">
                ...
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={`lt-pagination__page${item === page ? " lt-pagination__page--active" : ""}`}
                onClick={() => safePage(item)}
              >
                {item}
              </button>
            ),
          )}
          {!totalCountExact && (
            <span className="lt-pagination__ellipsis">...</span>
          )}
        </div>

        <button
          type="button"
          className="lt-pagination__arrow-btn"
          disabled={!showNext}
          onClick={() => safePage(page + 1)}
          aria-label="Следующая страница"
        >
          <ArrowIcon direction="right" />
        </button>
      </div>

      <div className={`lt-pagination__size${sizeOpen ? " lt-pagination__size--open" : ""}`} ref={sizeRef}>
        <button
          type="button"
          className="lt-pagination__size-toggle"
          onClick={() => setSizeOpen((v) => !v)}
        >
          <span className="lt-pagination__size-label">Показывать по:</span>
          <span className="lt-pagination__size-value">{pageSize} стр.</span>
          <ChevronIcon className="lt-pagination__size-chevron" />
        </button>
        <div className="lt-pagination__size-menu">
          <div className="lt-pagination__size-menu-body">
            {PAGE_SIZE_OPTIONS.map((size) => (
              <button
                key={size}
                type="button"
                className={`lt-pagination__size-option${size === pageSize ? " lt-pagination__size-option--active" : ""}`}
                onClick={() => {
                  onPageSizeChange(size);
                  setSizeOpen(false);
                }}
              >
                {size} стр.
              </button>
            ))}
          </div>
          <div className="lt-pagination__size-arrow" />
        </div>
      </div>
    </nav>
  );
}
